import { useState, useCallback, useMemo } from 'react';
import { FlaskConical, Check, HelpCircle, Send } from 'lucide-react';
import LabBench from '../../LabBench';
import { TestTubeRack } from '@/components/lab';
import InfoBox from '../../InfoBox';

// Educational content for each step
const stepInfoContent: Record<number, {
  title: string;
  content: string;
  whatHappens: string;
  observe: string[];
  equation?: string;
}> = {
  1: {
    title: 'Receiving the Unknown Sample',
    content: 'In qualitative analysis, unknowns may contain one or more cations that need to be identified. A systematic approach is essential - you must test for all possible cations regardless of early results because multiple ions may be present.',
    whatHappens: 'You receive an unknown solution that may contain 1 or 2 cations from the group: Cu²⁺, Ag⁺, Ni²⁺, or Al³⁺. You will need to perform all confirmation tests to identify what is present.',
    observe: [
      'Unknown solution may be colored (if Cu²⁺ or Ni²⁺ present)',
      'Colorless unknown could contain Ag⁺ and/or Al³⁺',
      'Initial observation provides clues but not definitive answers'
    ]
  },
  2: {
    title: 'Testing for Copper (Cu²⁺)',
    content: 'The copper test uses concentrated ammonia (NH₄OH) to form the deep blue tetraamminecopper(II) complex [Cu(NH₃)₄]²⁺. This is a highly specific test - no other common cation produces this distinctive deep blue colour with excess ammonia.',
    whatHappens: 'You perform the copper confirmation test by adding concentrated NH₄OH until basic. If Cu²⁺ is present, a deep blue colour develops. A negative result (no blue colour) means copper is absent.',
    observe: [
      'Positive: Deep blue colour',
      'Negative: No blue color change',
      'Initial light blue may darken with ammonia'
    ],
    equation: 'Cu²⁺ + 4NH₃ → [Cu(NH₃)₄]²⁺ (deep blue)'
  },
  3: {
    title: 'Testing for Silver (Ag⁺)',
    content: 'The silver test uses dilute HCl to precipitate silver chloride (AgCl). AgCl is extremely insoluble (Ksp = 1.77 x 10^-10) and forms immediately as a white precipitate. This is definitive for silver.',
    whatHappens: 'You perform the silver confirmation test by adding HCl. If Ag⁺ is present, a white precipitate of AgCl (silver chloride) forms immediately. No precipitate means silver is absent.',
    observe: [
      'Positive: White precipitate of AgCl',
      'Negative: Solution remains clear',
      'Precipitate may darken in light (photosensitive)'
    ],
    equation: 'Ag⁺ + Cl- → AgCl↓ (white precipitate)'
  },
  4: {
    title: 'Testing for Nickel (Ni²⁺)',
    content: 'The nickel test uses DMG (dimethylglyoxime) in basic solution to form a characteristic red precipitate of nickel dimethylglyoximate. This test is highly selective - DMG only produces this colour with nickel.',
    whatHappens: 'You perform the nickel confirmation test by making the solution basic with 6M NH₄OH, adding DMG, and stirring. If Ni²⁺ is present, a red precipitate forms. No red colour means nickel is absent.',
    observe: [
      'Positive: Red precipitate of nickel dimethylglyoximate',
      'Negative: No red color develops',
      'Must be in basic solution for test to work'
    ],
    equation: 'Ni²⁺ + 2 DMG → Ni(DMG)2↓ (red precipitate)'
  },
  5: {
    title: 'Testing for Aluminum (Al³⁺)',
    content: 'The aluminum test uses aluminon (aurintricarboxylic acid) in a spot test at pH ~6. Aluminum forms a red "lake" with aluminon that persists after washing. The lake is an insoluble dye-metal complex.',
    whatHappens: 'You perform the aluminum spot test by placing a drop on filter paper, adding aluminon, waiting, then washing. If Al³⁺ is present, a bright red spot remains. Complete color removal means aluminum is absent.',
    observe: [
      'Positive: Red spot remains after washing',
      'Negative: Color washes away completely',
      'Test performed at pH ~6 on filter paper'
    ],
    equation: 'Al³⁺ + Aluminon → Al-Aluminon lake (red)'
  },
  6: {
    title: 'Submitting Your Identification',
    content: 'Based on all your test results, you must identify which cation(s) are present in the unknown. A positive test result (characteristic color or precipitate) indicates the cation is present. Negative results indicate absence. Use all evidence to make your final determination.',
    whatHappens: 'You review all test results and select which cation(s) you believe are present. Your identification will be checked against the actual composition of the unknown.',
    observe: [
      'Review all positive and negative results',
      'Select cations with POSITIVE test results',
      'Consider that 1 or 2 cations may be present'
    ]
  }
};

interface Part6UnknownProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

type CationId = 'cu' | 'ag' | 'ni' | 'al';
type TestResult = 'positive' | 'negative' | null;

interface TestState {
  cu: TestResult;
  ag: TestResult;
  ni: TestResult;
  al: TestResult;
}

const cationInfo: Record<CationId, { name: string; symbol: string; positiveColor: string; testDescription: string }> = {
  cu: { name: 'Copper(II)', symbol: 'Cu²⁺', positiveColor: '#1565C0', testDescription: 'Deep blue colour with conc. NH₄OH' },
  ag: { name: 'Silver', symbol: 'Ag⁺', positiveColor: '#FAFAFA', testDescription: 'White precipitate of AgCl with HCl' },
  ni: { name: 'Nickel(II)', symbol: 'Ni²⁺', positiveColor: '#E53935', testDescription: 'Red precipitate with 6M NH₄OH + DMG' },
  al: { name: 'Aluminum', symbol: 'Al³⁺', positiveColor: '#F44336', testDescription: 'Red spot with aluminon' },
};

export default function Part6Unknown({ onStepComplete, currentStep = 1 }: Part6UnknownProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [unknownReceived, setUnknownReceived] = useState(false);
  const [testResults, setTestResults] = useState<TestState>({ cu: null, ag: null, ni: null, al: null });
  const [currentTest, setCurrentTest] = useState<CationId | null>(null);
  const [selectedIdentifications, setSelectedIdentifications] = useState<CationId[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);

  // Generate random unknown (1 or 2 cations)
  const unknownCations = useMemo(() => {
    const allCations: CationId[] = ['cu', 'ag', 'ni', 'al'];
    const shuffled = [...allCations].sort(() => Math.random() - 0.5);
    const numCations = Math.random() > 0.5 ? 2 : 1;
    return shuffled.slice(0, numCations);
  }, []);

  const allTestsComplete = Object.values(testResults).every(r => r !== null);

  const handleReceiveUnknown = useCallback(() => {
    setUnknownReceived(true);
    if (onStepComplete) onStepComplete('unknown-1');
  }, [onStepComplete]);

  const handleStartTest = useCallback((cationId: CationId) => {
    setCurrentTest(cationId);
  }, []);

  const handleCompleteTest = useCallback((cationId: CationId) => {
    const isPositive = unknownCations.includes(cationId);
    setTestResults(prev => ({ ...prev, [cationId]: isPositive ? 'positive' : 'negative' }));
    setCurrentTest(null);

    const stepMap: Record<CationId, string> = {
      cu: 'unknown-2',
      ag: 'unknown-3',
      ni: 'unknown-4',
      al: 'unknown-5',
    };
    if (onStepComplete) onStepComplete(stepMap[cationId]);
  }, [unknownCations, onStepComplete]);

  const handleToggleIdentification = useCallback((cationId: CationId) => {
    setSelectedIdentifications(prev => {
      if (prev.includes(cationId)) {
        return prev.filter(id => id !== cationId);
      }
      return [...prev, cationId];
    });
  }, []);

  const handleSubmit = useCallback(() => {
    const correct =
      selectedIdentifications.length === unknownCations.length &&
      selectedIdentifications.every(id => unknownCations.includes(id));
    setIsCorrect(correct);
    setSubmitted(true);
    if (onStepComplete) onStepComplete('unknown-6');
  }, [selectedIdentifications, unknownCations, onStepComplete]);

  // Test tube colors based on test results
  const getTestTubeColor = (cationId: CationId) => {
    const result = testResults[cationId];
    if (result === 'positive') return cationInfo[cationId].positiveColor;
    if (result === 'negative') return '#EEEEEE';
    return '#F5F5F5';
  };

  return (
    <LabBench
      experimentName="Unknown Cation Identification"
      completedCount={[unknownReceived, testResults.cu, testResults.ag, testResults.ni, testResults.al, submitted].filter(Boolean).length}
      totalSteps={6}
      equipment={['Unknown solution', 'All confirmation reagents']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="unknown-test">
        {/* Unknown Sample */}
        {!unknownReceived ? (
          <div className="receive-panel">
            <div className="info-header">
              <HelpCircle size={24} />
              <h3>Unknown Cation Identification</h3>
              <button
                className="step-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowInfo(1); }}
              >
                ?
              </button>
            </div>
            <p>
              Your unknown may contain <strong>1 or 2 cations</strong> from: Al³⁺, Ag⁺, Cu²⁺, Ni²⁺.
              Use the confirmation tests you learned to systematically identify all cations present.
            </p>
            <button className="action-btn" onClick={handleReceiveUnknown}>
              <FlaskConical size={16} />
              Receive Unknown Sample
            </button>
          </div>
        ) : (
          <>
            {/* Unknown Sample Display */}
            <div className="unknown-sample">
              <svg viewBox="0 0 60 100" width={55} height={90}>
                <defs>
                  <linearGradient id="unknown-glass" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                    <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                    <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                  </linearGradient>
                </defs>
                <ellipse cx="30" cy="12" rx="14" ry="5" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />
                <path d="M16 12 L16 78 Q16 90 30 90 Q44 90 44 78 L44 12" fill="url(#unknown-glass)" stroke="#8BA8C4" strokeWidth="1.5" />
                <path d="M18 35 L18 76 Q18 87 30 87 Q42 87 42 76 L42 35 Z" fill="#E8E8E8" opacity="0.8" />
                <text x="30" y="60" fontSize="10" fill="#666" textAnchor="middle" fontWeight="bold">?</text>
                <line x1="20" y1="18" x2="20" y2="70" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <span className="sample-label">Unknown Sample</span>
            </div>

            {/* Test Results Grid */}
            <div className="tests-grid">
              {(Object.keys(cationInfo) as CationId[]).map(cationId => {
                const info = cationInfo[cationId];
                const result = testResults[cationId];
                const isTesting = currentTest === cationId;

                return (
                  <div key={cationId} className={`test-card ${result ? 'completed' : ''} ${isTesting ? 'testing' : ''}`}>
                    <div className="test-header">
                      <span className="cation-symbol">{info.symbol}</span>
                      <span className="cation-name">{info.name}</span>
                      <button
                        className="step-info-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          const stepMap: Record<CationId, number> = { cu: 2, ag: 3, ni: 4, al: 5 };
                          setShowInfo(stepMap[cationId]);
                        }}
                      >
                        ?
                      </button>
                    </div>

                    {/* Mini test tube */}
                    <div className="mini-tube">
                      <svg viewBox="0 0 30 50" width={30} height={50}>
                        <ellipse cx="15" cy="8" rx="8" ry="3" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1" />
                        <path d="M7 8 L7 40 Q7 47 15 47 Q23 47 23 40 L23 8" fill="rgba(200,220,240,0.3)" stroke="#8BA8C4" strokeWidth="1" />
                        {result && (
                          <path
                            d="M9 25 L9 38 Q9 44 15 44 Q21 44 21 38 L21 25 Z"
                            fill={getTestTubeColor(cationId)}
                            opacity="0.9"
                          />
                        )}
                      </svg>
                    </div>

                    <p className="test-desc">{info.testDescription}</p>

                    {result === null ? (
                      isTesting ? (
                        <button className="test-btn testing" onClick={() => handleCompleteTest(cationId)}>
                          Complete Test
                        </button>
                      ) : (
                        <button className="test-btn" onClick={() => handleStartTest(cationId)}>
                          Perform Test
                        </button>
                      )
                    ) : (
                      <div className={`result-badge ${result}`}>
                        {result === 'positive' ? 'POSITIVE' : 'NEGATIVE'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Identification Selection */}
            {allTestsComplete && !submitted && (
              <div className="identification-panel">
                <div className="identification-header">
                  <h4>Select the cation(s) present in your unknown:</h4>
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(6); }}
                  >
                    ?
                  </button>
                </div>
                <div className="identification-options">
                  {(Object.keys(cationInfo) as CationId[]).map(cationId => {
                    const info = cationInfo[cationId];
                    const isSelected = selectedIdentifications.includes(cationId);
                    const result = testResults[cationId];

                    return (
                      <button
                        key={cationId}
                        className={`id-option ${isSelected ? 'selected' : ''} ${result === 'positive' ? 'positive' : ''}`}
                        onClick={() => handleToggleIdentification(cationId)}
                      >
                        <span className="check-box">{isSelected && <Check size={14} />}</span>
                        <span>{info.symbol}</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  className="submit-btn"
                  onClick={handleSubmit}
                  disabled={selectedIdentifications.length === 0}
                >
                  <Send size={16} />
                  Submit Identification
                </button>
              </div>
            )}

            {/* Results */}
            {submitted && (
              <div className={`submission-result ${isCorrect ? 'correct' : 'incorrect'}`}>
                <div className="result-icon">
                  {isCorrect ? <Check size={32} /> : <HelpCircle size={32} />}
                </div>
                <div className="result-content">
                  <h4>{isCorrect ? 'Correct Identification!' : 'Incorrect Identification'}</h4>
                  <p>
                    The unknown contained: <strong>{unknownCations.map(id => cationInfo[id].symbol).join(' and ')}</strong>
                  </p>
                  <p>
                    You identified: <strong>{selectedIdentifications.map(id => cationInfo[id].symbol).join(' and ') || 'None'}</strong>
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Theory Box */}
        <div className="theory-box">
          <h4>Systematic Analysis</h4>
          <p>
            When identifying unknowns, always perform all confirmation tests regardless of earlier results.
            Some unknowns may contain multiple cations, and systematic testing ensures you identify all of them.
          </p>
        </div>

        <style>{`
          .unknown-test {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 700px;
          }

          .receive-panel, .identification-panel {
            width: 100%;
            background: var(--bg-elevated);
            border-radius: 12px;
            padding: 20px;
          }

          .info-header {
            display: flex;
            align-items: center;
            gap: 12px;
            color: var(--primary);
            margin-bottom: 16px;
          }

          .info-header h3 {
            margin: 0;
            font-size: 16px;
          }

          .receive-panel p {
            font-size: 13px;
            color: var(--text-primary);
            line-height: 1.6;
            margin-bottom: 16px;
          }

          .action-btn, .submit-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 24px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn:hover, .submit-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .submit-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none;
          }

          .unknown-sample {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .sample-label {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .tests-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
            width: 100%;
          }

          .test-card {
            background: var(--bg-elevated);
            border-radius: 12px;
            padding: 16px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            border: 2px solid transparent;
            transition: all 0.2s;
          }

          .test-card.testing {
            border-color: var(--primary);
          }

          .test-card.completed {
            border-color: var(--border);
          }

          .test-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            width: 100%;
          }

          .test-header .step-info-btn {
            position: absolute;
            top: 0;
            right: 0;
          }

          .cation-symbol {
            font-size: 16px;
            font-weight: 700;
            color: var(--text-primary);
          }

          .cation-name {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .mini-tube {
            margin: 8px 0;
          }

          .test-desc {
            font-size: 11px;
            color: var(--text-muted);
            text-align: center;
            margin: 0;
          }

          .test-btn {
            padding: 8px 16px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .test-btn:hover {
            background: var(--primary-dark);
          }

          .test-btn.testing {
            background: #00A86B;
          }

          .result-badge {
            padding: 6px 12px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
          }

          .result-badge.positive {
            background: rgba(0, 168, 107, 0.1);
            color: #00A86B;
          }

          .result-badge.negative {
            background: rgba(158, 158, 158, 0.1);
            color: #9E9E9E;
          }

          .identification-panel h4 {
            margin: 0 0 16px 0;
            font-size: 14px;
            color: var(--text-primary);
          }

          .identification-options {
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
            margin-bottom: 16px;
          }

          .id-option {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            background: var(--bg-dark);
            border: 2px solid var(--border);
            border-radius: 8px;
            font-size: 13px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .id-option:hover {
            border-color: var(--primary);
          }

          .id-option.selected {
            border-color: var(--primary);
            background: rgba(99, 102, 241, 0.1);
          }

          .id-option.positive {
            border-color: #00A86B;
          }

          .check-box {
            width: 18px;
            height: 18px;
            border: 2px solid var(--border);
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .id-option.selected .check-box {
            background: var(--primary);
            border-color: var(--primary);
            color: white;
          }

          .submission-result {
            display: flex;
            gap: 16px;
            padding: 20px;
            border-radius: 12px;
            width: 100%;
          }

          .submission-result.correct {
            background: rgba(0, 168, 107, 0.1);
          }

          .submission-result.incorrect {
            background: rgba(255, 90, 54, 0.1);
          }

          .result-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 48px;
            height: 48px;
            border-radius: 50%;
          }

          .submission-result.correct .result-icon {
            background: #00A86B;
            color: white;
          }

          .submission-result.incorrect .result-icon {
            background: #FF5A36;
            color: white;
          }

          .result-content h4 {
            margin: 0 0 8px 0;
            font-size: 16px;
          }

          .submission-result.correct .result-content h4 {
            color: #00A86B;
          }

          .submission-result.incorrect .result-content h4 {
            color: #FF5A36;
          }

          .result-content p {
            margin: 0 0 4px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .theory-box {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box p {
            margin: 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }

          .identification-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 16px;
          }

          .identification-header h4 {
            margin: 0;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp2"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
